"use client";

/**
 * Client-side carrier for which optional settings sections this DEPLOYMENT can
 * serve.
 *
 * Same shape and reasoning as AppLinkOriginProvider: the value is runtime
 * server config (env, read in the root layout), never NEXT_PUBLIC — the built
 * artifact is promoted byte-for-byte from dev to prod, so anything baked in at
 * build time would be wrong in one of the two environments.
 *
 * What it gates is visibility, not access. The API routes refuse independently
 * (an unconfigured deployment cannot start an OAuth flow whatever the UI says);
 * this exists so an environment without credentials doesn't advertise a tab
 * whose only button fails.
 *
 * Default = every capability off. A missing provider hides optional sections
 * rather than showing broken ones.
 */

import { createContext, useContext, type ReactNode } from "react";
import {
  NO_SETTINGS_CAPABILITIES,
  availableIntegrations,
  visibleSettingsSections,
  type SettingsCapabilities,
  type SettingsSectionDef,
} from "@/lib/settings-sections";
import type { IntegrationDef } from "@/lib/integrations";

const SettingsCapabilitiesContext = createContext<SettingsCapabilities>(NO_SETTINGS_CAPABILITIES);

export function SettingsCapabilitiesProvider({
  capabilities,
  children,
}: {
  capabilities: SettingsCapabilities;
  children: ReactNode;
}) {
  return (
    <SettingsCapabilitiesContext.Provider value={capabilities}>
      {children}
    </SettingsCapabilitiesContext.Provider>
  );
}

/**
 * The providers to offer this instance.
 *
 * Sections are no longer capability-filtered — every one of them is
 * unconditional now that the providers live behind a single Integrations entry
 * — so this hook serves that section's contents instead. The sidenav, the
 * mobile tabs and the Settings dropdown read SETTINGS_SECTIONS directly.
 */
export const useVisibleIntegrations = (): readonly IntegrationDef[] =>
  availableIntegrations(useContext(SettingsCapabilitiesContext));

/** The sections to render — one list for the sidenav, the mobile tabs and the
 *  Settings dropdown, so they cannot disagree about whether Integrations is
 *  there. */
export const useVisibleSettingsSections = (): readonly SettingsSectionDef[] =>
  visibleSettingsSections(useContext(SettingsCapabilitiesContext));
